import {LocalOfferOutlined} from '@mui/icons-material';
import {Box, Typography} from '@mui/material';
import {FC, useMemo} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {DeleteTariff} from 'src/components/tariffs/DeleteTariff';
import {TariffStatusButton} from 'src/components/tariffs/TariffStatusButton';
import {LanguageEnum, TariffsStatusEnum} from 'src/enums';
import {useCan} from 'src/permissions/hooks';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {RecordActionBar} from 'src/components/mobile/RecordActionBar';
import {actionSlotSx} from 'src/components/mobile/RecordActionBar/RecordActionBar.sx';
import {RecordIdentityCard} from 'src/components/mobile/RecordIdentityCard';
import {ProceedCurrenciesEnum} from 'src/utils/consts';
import {
    accentBarSx,
    badgeSx,
    cardSx,
    factLabelSx,
    factSx,
    factValueSx,
    factsSx,
    feeAmountSx,
    feeCurrencySx,
    feePeriodSx,
    feeSx,
    headerSx,
    statusPillSx,
    subtitleSx,
    titleSx,
} from './TariffIdentityCard.sx';
import {TariffIdentityCardProps} from './TariffIdentityCard.types';

/**
 * What this plan is, in one card.
 *
 * Four facts, each printed once. The monthly fee used to appear twice inside the same panel — as a
 * headline figure and again in a list below it — and the country and region never appeared at all,
 * though the tariffs list filters by them, so a reader arriving from that list could not see which
 * values this plan had matched. The contract count is the page's own subject: the table further down
 * is that list.
 */
export const TariffIdentityCard: FC<TariffIdentityCardProps> = ({data, language}) => {
    const intl = useIntl();
    // One guard used to cover both buttons, and it was `can('delete','tariffs')` - which CASL
    // answered true for anyone holding `tariffs.update`. Each button names its own endpoint's
    // permission; that split is kept exactly as it was.
    const canToggleTariff = useCan('tariffs.toggleStatus');
    const canDeleteTariff = useCan('tariffs.delete');

    const name = useMemo(() => {
        const byLanguage: Partial<Record<LanguageEnum, string>> = {
            [LanguageEnum.ENGLISH]: data.name,
            [LanguageEnum.FRENCH]: data.nameFrench,
            [LanguageEnum.DEUTSCH]: data.nameDeutsch,
        };

        // The old header refused to show a name equal to the string "undefined undefined", which is
        // what a missing first and last name used to serialise to. A tariff has one name field, so
        // the guard is on the translation being absent rather than on that sentinel.
        return byLanguage[language ?? LanguageEnum.ENGLISH] || data.name;
    }, [data, language]);

    const region = useMemo(
        () => [data.country, data.region].filter(Boolean).join(' · ') || '—',
        [data.country, data.region]
    );

    const isActive = data.status === TariffsStatusEnum.ACTIVE;
    const {isCompact} = useScreenTier();

    /** The plan's state, drawn once: beside the name on a desktop, as the card's status on a phone. */
    const statusPill = (
        <Box component="span" sx={statusPillSx(isActive)}>
            <FormattedMessage id={isActive ? 'active' : 'deactivated'}/>
        </Box>
    );

    if (isCompact) {
        return (
            <>
                <RecordIdentityCard
                    icon={<LocalOfferOutlined/>}
                    title={name}
                    subtitle={(
                        <>
                            <FormattedMessage id="tariffPlan"/>
                            {region !== '—' && ` · ${region}`}
                        </>
                    )}
                    status={statusPill}
                    facts={[
                        {
                            id: 'monthlyFee',
                            label: <FormattedMessage id="monthlyFeeWithoutCurrency"/>,
                            // The same two formatters the card below uses: the currency symbol, and
                            // the fee divided by 100 because it is stored in cents.
                            value: `${ProceedCurrenciesEnum[data.currency]} ${data.monthlyFee / 100}`,
                        },
                        {
                            id: 'minDuration',
                            label: <FormattedMessage id="minDuration"/>,
                            value: intl.formatMessage({id: 'monthsTariff'}, {count: data.contractPeriodLimit}),
                        },
                        {
                            id: 'region',
                            label: <FormattedMessage id="region"/>,
                            value: region,
                        },
                        {
                            id: 'contracts',
                            label: <FormattedMessage id="contracts"/>,
                            value: data.contractsSize ?? 0,
                        },
                    ]}
                />
                {(canToggleTariff || canDeleteTariff) && (
                    <RecordActionBar>
                        {/* The same two buttons, behind the same two permissions. Activating the plan
                            is what the page is for, so it takes the width; deleting it keeps the
                            brand gradient its own component already gives it. */}
                        {canToggleTariff && (
                            <Box sx={actionSlotSx(true)}>
                                <TariffStatusButton
                                    tariff={data}
                                    toolbar
                                />
                            </Box>
                        )}
                        {canDeleteTariff && (
                            <Box sx={actionSlotSx()}>
                                <DeleteTariff
                                    row={data}
                                    toolbar
                                />
                            </Box>
                        )}
                    </RecordActionBar>
                )}
            </>
        );
    }

    return (
        <Box sx={cardSx}>
            <Box sx={accentBarSx}/>

            <Box sx={headerSx}>
                <Box sx={badgeSx}>
                    <LocalOfferOutlined/>
                </Box>
                <Box sx={{minWidth: 0}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap'}}>
                        <Typography sx={titleSx}>{name}</Typography>
                        {statusPill}
                    </Box>
                    <Typography sx={subtitleSx}>
                        <FormattedMessage id="tariffPlan"/>
                        {region !== '—' && ` · ${region}`}
                    </Typography>
                </Box>

                <Box sx={{flex: '1 1 auto'}}/>

                {canToggleTariff && <TariffStatusButton tariff={data} toolbar/>}
                {canDeleteTariff && <DeleteTariff row={data} toolbar/>}
            </Box>

            <Box sx={factsSx}>
                <Box sx={factSx}>
                    <Typography sx={factLabelSx}>
                        <FormattedMessage id="monthlyFeeWithoutCurrency"/>
                    </Typography>
                    <Box sx={feeSx}>
                        <Typography component="span" sx={feeCurrencySx}>
                            {ProceedCurrenciesEnum[data.currency]}
                        </Typography>
                        {/* Stored in cents, like every other invoiced amount in the product. */}
                        <Typography component="span" sx={feeAmountSx}>{data.monthlyFee / 100}</Typography>
                        <Typography component="span" sx={feePeriodSx}>
                            {intl.formatMessage({id: 'monthlyFeeDescription'})}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={factSx}>
                    <Typography sx={factLabelSx}>
                        <FormattedMessage id="minDuration"/>
                    </Typography>
                    <Typography sx={factValueSx}>
                        {intl.formatMessage({id: 'monthsTariff'}, {count: data.contractPeriodLimit})}
                    </Typography>
                </Box>

                <Box sx={factSx}>
                    <Typography sx={factLabelSx}>
                        <FormattedMessage id="region"/>
                    </Typography>
                    <Typography sx={factValueSx}>{region}</Typography>
                </Box>

                <Box sx={factSx}>
                    <Typography sx={factLabelSx}>
                        <FormattedMessage id="contracts"/>
                    </Typography>
                    <Typography sx={{...factValueSx as object, fontVariantNumeric: 'tabular-nums'}}>
                        {data.contractsSize ?? 0}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};
